import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse.js";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { ButtonUtils, ContextUtils, ObjectBuilder, Image3D, MeshUtils, Rectangle3D, ScrollViewer3D, WrapPanel } from "@dreamcam/babylon-tools";
import { BroadcastManagerState } from "@dreamcam/web-sdk";
import { Env } from "../../settings/Env";
import { text } from "../../TextResources";
import { MeshName } from "../MeshName";
import { Vector2WithInfo } from "@babylonjs/gui/2D/math2D.js";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { StringUtils } from "../utils/StringUtils";
import { ScrollHandler } from "../ScrollHandler";
import { XRInputHelper } from "../XRInputHelper";
import { LayoutComponentHelper } from "./LayoutComponentHelper";
import { AppView } from "../AppView";
import { MessageType } from "../../interfaces/NotificationCenter";
export class BroadcastMenuModelProfile {
    get isVisible() {
        return this._mesh.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        if (value) {
            this._scrollHandler.addThumbstickAxesChangedListener();
        }
        else {
            this._scrollHandler.releaseThumbstickAxesChangedListener();
            this._messageDialog.hide();
        }
        MeshUtils.setVisibility(this._mesh, value);
    }
    constructor(mesh, menuFunction, callbacks, messageDialog) {
        this._modelFullProfilePanelTagSections = new Map();
        this._modelFullProfilePanelTextSections = new Map();
        this._displayedPosts = { first: 0, last: 0, total: 0, postIds: [] };
        this._scrollDirection = "none";
        this._isGuest = false;
        this._currentState = BroadcastManagerState.UNINITIALIZED;
        this._profilePfpUrl = "";
        this._modelId = "";
        this._modelNickname = "";
        this._modelCategory = undefined;
        this._postImages = [];
        this._lockPanels = [];
        this._postLimit = 5;
        this._scrollDownThreshold = 0.95;
        this._scrollUpThreshold = 0.25;
        this._displayPostLimit = 25;
        this.statusImage = Env.broadcast.menu.modelProfile.status;
        this.statusMap = {
            [BroadcastManagerState.AWAY]: { color: this.statusImage.background.away, text: text.lobby_gallery_item_status_away },
            [BroadcastManagerState.PRIVATE_SHOW]: { color: this.statusImage.background.private, text: text.lobby_gallery_item_status_privateShow },
            [BroadcastManagerState.MY_PRIVATE_SHOW]: { color: this.statusImage.background.private, text: text.lobby_gallery_item_status_privateShow },
            [BroadcastManagerState.SPY_SHOW]: { color: this.statusImage.background.private, text: text.lobby_gallery_item_status_privateShow },
            [BroadcastManagerState.PUBLIC]: { color: this.statusImage.background.live, text: text.lobby_gallery_item_status_live },
            [BroadcastManagerState.OFFLINE]: { color: this.statusImage.background.offline, text: text.lobby_gallery_item_status_offline },
        };
        this._onTagClickHandler = async (eventData, tag, _isToggled) => {
            if (eventData.buttonIndex !== 0)
                return;
            const textureHeight = this._modelFullProfilePanel.host.getSize().height;
            const deltaMeshY = (textureHeight / 2 - eventData.y) * Env.broadcast.menu.modelProfile.height / textureHeight + Env.broadcast.menu.modelProfile.tag.tagHeight + Env.broadcast.menu.tipMenu.message.height / 2;
            const messageDialogAbsolutePosition = Vector3.TransformCoordinates(new Vector3(0, deltaMeshY, Env.broadcast.menu.tipMenu.message.z), this._mesh.mesh._localMatrix);
            const categoryCodes = this._modelCategory ?? "";
            // const tagCodes = isToggled ? (this._tagsFilter?.split(",").filter(x => x != tag.code).join(",") ?? "") : tag.code;
            const tagCodes = tag.code;
            if (this._currentState === BroadcastManagerState.MY_PRIVATE_SHOW || this._currentState === BroadcastManagerState.SPY_SHOW) {
                this._messageDialog.openConfirmLeaveMessage(messageDialogAbsolutePosition, this._mesh.mesh.rotationQuaternion ?? this._mesh.mesh.rotation.toQuaternion(), async () => {
                    this._messageDialog.hide();
                    await this._openLobbyAsync(categoryCodes, tagCodes, this._streamTypesFilter ?? "");
                });
            }
            else {
                await this._openLobbyAsync(categoryCodes, tagCodes, this._streamTypesFilter ?? "");
            }
        };
        this._mesh = mesh;
        this._menuFunctions = menuFunction;
        this._callbacks = callbacks;
        this._messageDialog = messageDialog;
        const m = Env.broadcast.menu;
        const mp = m.modelProfile;
        this._scrollHandler = new ScrollHandler([MeshName.broadcastMenuPanelModelProfile], this._mesh.m2px(mp.scrollView.speed), (v) => {
            this._modelPostsScrollViewer.verticalValue = Math.max(Math.min(this._modelPostsScrollViewer.verticalValue + v / (this._modelPostsScrollViewer.children[0].heightInPixels - this._modelPostsScrollViewer.heightInPixels), 1), 0);
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh);
        pointerAddon.onPointerOut.add(() => {
            this._scrollHandler.stopScroll();
        });
        const rootPanelContainer = new Rectangle3D("modelProfileRootContainer");
        rootPanelContainer.widthInPixels = this._mesh.m2px(mp.width);
        rootPanelContainer.heightInPixels = this._mesh.m2px(mp.height);
        rootPanelContainer.cornerRadius = this._mesh.m2px(mp.cornerRadius);
        rootPanelContainer.clipChildren = true;
        rootPanelContainer.thickness = 0;
        this._mesh.texture.addControl(rootPanelContainer);
        const backgroundPanel = new Rectangle3D("modelProfilePanelBackground");
        backgroundPanel.widthInPixels = this._mesh.m2px(mp.width);
        backgroundPanel.heightInPixels = this._mesh.m2px(mp.height);
        backgroundPanel.cornerRadius = this._mesh.m2px(mp.cornerRadius);
        backgroundPanel.background = m.backgroundColor;
        backgroundPanel.thickness = 0;
        rootPanelContainer.addControl(backgroundPanel);
        const profileHeaderContainer = new Container("modelProfileHeaderContainer");
        profileHeaderContainer.heightInPixels = this._mesh.m2px(mp.headerHeight);
        profileHeaderContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        profileHeaderContainer.background = mp.headerBackground;
        rootPanelContainer.addControl(profileHeaderContainer);
        this._headerText = ObjectBuilder.createTextBlock3D(this._mesh, "modelProfileHeaderText", text.broadcast_menu_modelPanel_profilePanel_profile, mp.header);
        profileHeaderContainer.addControl(this._headerText);
        this._backButton = ButtonUtils.CreateImgButton("modelPanelBack", mp.backButtonLayout, mesh);
        this._backButton.onClickObserver.add(async () => {
            this._headerText.text = text.broadcast_menu_modelPanel_profilePanel_profile;
            this._modelFullProfilePanel.isVisible = false;
            this._modelPostsScrollViewer.heightInPixels = this._mesh.m2px(mp.height - mp.headerHeight);
            this._modelPostsScrollViewer.topInPixels = this._mesh.m2px(mp.headerHeight);
            this._backButton.isVisible = false;
            this._modelInfoContainer.isVisible = true;
            this.createShowMoreButton();
        });
        this._backButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._backButton.isVisible = false;
        profileHeaderContainer.addControl(this._backButton);
        const closeButton = ButtonUtils.CreateImgButton("modelPanelClose", mp.closeButtonLayout, mesh);
        closeButton.onClickObserver.add(async () => {
            setTimeout(() => {
                this._menuFunctions.setModelProfilePanelVisibility(false);
                this._menuFunctions.updateModelProfilePanel();
            }, 0);
        });
        closeButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        profileHeaderContainer.addControl(closeButton);
        this._modelPostsScrollViewer = new ScrollViewer3D("modelProfileScrollViewer");
        this._modelPostsScrollViewer.widthInPixels = this._mesh.m2px(mp.width);
        this._modelPostsScrollViewer.heightInPixels = this._mesh.m2px(mp.height - mp.headerHeight);
        this._modelPostsScrollViewer.topInPixels = this._mesh.m2px(mp.headerHeight);
        this._modelPostsScrollViewer.color = mp.scrollView.colors.background;
        this._modelPostsScrollViewer.barColor = mp.scrollView.colors.bar;
        this._modelPostsScrollViewer.hoverBarColor = mp.scrollView.colors.hoverBar;
        this._modelPostsScrollViewer.pressedBarColor = mp.scrollView.colors.pressedBar;
        this._modelPostsScrollViewer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        this._modelPostsScrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        this._modelPostsScrollViewer.verticalBarPaddingInPixels = {
            left: this._mesh.m2px(mp.scrollView.verticalBar.padding.left),
            right: this._mesh.m2px(mp.scrollView.verticalBar.padding.right),
            top: this._mesh.m2px(mp.scrollView.verticalBar.padding.top),
            bottom: this._mesh.m2px(mp.scrollView.verticalBar.padding.bottom)
        };
        this._modelPostsScrollViewer.barSizeInPixels = this._mesh.m2px(mp.scrollView.verticalBar.size);
        rootPanelContainer.addControl(this._modelPostsScrollViewer);
        this._profileAndPostsPanel = new StackPanel("modelProfileAndPostsPanel");
        this._modelPostsScrollViewer.addControl(this._profileAndPostsPanel);
        this._modelPostsScrollViewer.autoSizeThumbs = true;
        let prevScrollViewerValue = 0;
        this._modelPostsScrollViewer.verticalValue = 0;
        this._modelPostsScrollViewer.onVerticalValueChangedObservable.add(async (v) => {
            if (v > prevScrollViewerValue && v >= this._scrollDownThreshold && this._displayedPosts.last !== this._lastRequestedPostsOffset) {
                this._scrollDirection = "down";
                this._lastRequestedPostsOffset = this._displayedPosts.last;
                await this._menuFunctions.getModelPostsAsync(this._postLimit, this._displayedPosts.last);
            }
            else if (v < prevScrollViewerValue && v <= this._scrollUpThreshold && this._displayedPosts.first !== 0 && Math.max(this._displayedPosts.first - this._postLimit, 0) !== this._lastRequestedPostsOffset) {
                this._scrollDirection = "up";
                const firstPost = Math.max(this._displayedPosts.first - this._postLimit, 0);
                this._lastRequestedPostsOffset = firstPost;
                await this._menuFunctions.getModelPostsAsync(Math.min(this._postLimit, firstPost), firstPost);
            }
            prevScrollViewerValue = v;
        });
        this._addModelProfilePanel(this._profileAndPostsPanel);
        this._addModelPostsPanel(this._profileAndPostsPanel);
        this.isVisible = false;
    }
    _addModelProfilePanel(parent) {
        const mp = Env.broadcast.menu.modelProfile;
        const profileContainer = new StackPanel("modelProfileContainer");
        profileContainer.isVertical = true;
        profileContainer.widthInPixels = this._mesh.m2px(mp.width);
        profileContainer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        profileContainer.paddingTopInPixels = this._mesh.m2px(mp.top);
        parent.addControl(profileContainer);
        this._modelInfoContainer = new StackPanel("modelInfoContainer");
        this._modelInfoContainer.spacing = this._mesh.m2px(0.012);
        profileContainer.addControl(this._modelInfoContainer);
        const modelProfileControls = LayoutComponentHelper.createModelProfileControl(this._mesh, this._modelInfoContainer, mp);
        this._outlineContainer = modelProfileControls.outlineContainer;
        this._modelPfp = modelProfileControls.modelProfileImage;
        this._statusContainer = modelProfileControls.statusTextContainer;
        this._statusText = modelProfileControls.statusText;
        this._modelName = ObjectBuilder.createTextBlock3D(this._mesh, "modelName", "", mp.name);
        this._modelInfoContainer.addControl(this._modelName);
        this._modelInfo = ObjectBuilder.createTextBlock3D(this._mesh, "modelInfo", "", mp.info);
        this._modelInfo.enableSymbolWrap = true;
        this._modelInfoContainer.addControl(this._modelInfo);
        profileContainer.addControl(ObjectBuilder.createTextBlock3D(this._mesh, "modelBioHeader", text.broadcast_menu_modelPanel_profilePanel_bio, mp.sectionHeader));
        this._modelBio = new WrapPanel("modelBio");
        this._modelBio.spacing = this._mesh.m2px(mp.spacing);
        this._modelBio.childrenSpacing = this._mesh.m2px(mp.spacing);
        this._modelBio.widthInPixels = this._mesh.m2px(mp.width - mp.left - mp.right);
        profileContainer.addControl(this._modelBio);
        this._modelFullProfilePanel = new StackPanel("modelFullProfilePanel");
        this._modelFullProfilePanel.isVertical = true;
        profileContainer.addControl(this._modelFullProfilePanel);
        this._modelFullProfilePanel.isVisible = false;
        this._checkMyVideosPanel = new Container("checkMyVideosPanel");
        this._checkMyVideosPanel.widthInPixels = this._mesh.m2px(mp.width);
        this._checkMyVideosPanel.heightInPixels = this._mesh.m2px(mp.checkMyVideosButton.panel.height);
        profileContainer.addControl(this._checkMyVideosPanel);
        const button = ButtonUtils.CreateImgButtonWithText("checkMyVideosButton", {
            imageLayout: mp.checkMyVideosButton.imageLayout,
            textLayout: mp.checkMyVideosButton.textLayout,
            text: text.broadcast_menu_modelPanel_profilePanel_check_videos,
        }, this._mesh);
        button.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        button.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        button.onClickObserver.add(async () => {
            const appView = AppView.getInstance();
            if (!this._menuFunctions.canLeaveBroadcast()) {
                const bm = appView.appController.sdk.broadcastManager;
                if (bm.state === BroadcastManagerState.MY_PRIVATE_SHOW || bm.state === BroadcastManagerState.SPY_SHOW) {
                    const infoText = bm.state === BroadcastManagerState.MY_PRIVATE_SHOW
                        ? text.broadcast_menu_privateShow_leaveWarning
                        : text.broadcast_menu_spyMode_leaveWarning;
                    appView.appController.notificationCenter?.showMessage(MessageType.info, infoText);
                }
                return;
            }
            this._callbacks.setGalleryModel({
                modelId: this._modelId,
                modelNickname: this._modelNickname,
                videoClipCount: 0,
            });
            appView.appController.scenesSwitcher.setLobbyPageParams({
                lobbyMode: "Videos",
                lobbyVideosMode: "AllVideos",
            });
            await this._callbacks.openLobbyAsync();
        });
        this._checkMyVideosPanel.addControl(button);
        this._modelPostsHeader = ObjectBuilder.createTextBlock3D(this._mesh, "modelPostsHeader", text.broadcast_menu_modelPanel_profilePanel_posts, mp.sectionHeader);
        this._modelPostsHeader.isVisible = false;
        profileContainer.addControl(this._modelPostsHeader);
    }
    _addFullModelProfilePanelTagSection(parent, label) {
        const mp = Env.broadcast.menu.modelProfile;
        const header = ObjectBuilder.createTextBlock3D(this._mesh, `modelProfile${label}Header`, label, mp.sectionHeader);
        parent.addControl(header);
        const panel = new WrapPanel(`modelProfile${label}Content`);
        panel.spacing = this._mesh.m2px(mp.spacing);
        panel.childrenSpacing = this._mesh.m2px(mp.spacing);
        panel.widthInPixels = this._mesh.m2px(mp.width - mp.left - mp.right);
        header.isVisible = false;
        panel.isVisible = false;
        parent.addControl(panel);
        return { header, panel };
    }
    _addFullModelProfilePanelTextSection(parent, label) {
        const mp = Env.broadcast.menu.modelProfile;
        const header = ObjectBuilder.createTextBlock3D(this._mesh, `modelProfile${label}Header`, label, mp.sectionHeader);
        header.isVisible = false;
        parent.addControl(header);
        const panel = ObjectBuilder.createTextBlock3D(this._mesh, `modelProfile${label}Content`, "", mp.section);
        panel.isVisible = false;
        parent.addControl(panel);
        return { header, panel };
    }
    _outputModelProfileTagSection(sectionName, ...data) {
        let section = this._modelFullProfilePanelTagSections.get(sectionName);
        if (section === undefined) {
            section = this._addFullModelProfilePanelTagSection(this._modelFullProfilePanel, sectionName);
            this._modelFullProfilePanelTagSections.set(sectionName, section);
            this._modelFullProfilePanel.addControl(section.header);
            this._modelFullProfilePanel.addControl(section.panel);
        }
        if (!data.some(s => s.text !== undefined && s.text !== null)) {
            section.header.isVisible = false;
            section.panel.isVisible = false;
            return;
        }
        else {
            section.header.isVisible = true;
            section.panel.isVisible = true;
        }
        for (const datum of data) {
            if (datum.text !== undefined && datum.text !== null)
                this.addTag(section.panel, datum);
        }
    }
    _outputModelProfileTextSection(sectionName, ...data) {
        let section = this._modelFullProfilePanelTextSections.get(sectionName);
        if (section === undefined) {
            section = this._addFullModelProfilePanelTextSection(this._modelFullProfilePanel, sectionName);
            this._modelFullProfilePanelTextSections.set(sectionName, section);
            this._modelFullProfilePanel.addControl(section.header);
            this._modelFullProfilePanel.addControl(section.panel);
        }
        if (!data.some(s => s !== undefined && s !== "" && s !== null)) {
            section.header.isVisible = false;
            section.panel.isVisible = false;
            return;
        }
        else {
            section.header.isVisible = true;
            section.panel.isVisible = true;
        }
        for (const datum of data) {
            if (datum !== undefined && datum !== "" && datum !== null)
                section.panel.text = datum;
        }
    }
    _addModelPostsPanel(parent) {
        this._modelPosts = new StackPanel("modelPostsPanel");
        this._modelPosts.isVertical = true;
        this._modelPosts.spacing = this._mesh.m2px(Env.broadcast.menu.modelProfile.post.bottom);
        this._modelPosts.paddingBottomInPixels = this._mesh.m2px(Env.broadcast.menu.modelProfile.post.bottom);
        parent.addControl(this._modelPosts);
    }
    outputModelPosts(posts) {
        if (posts.length === 0)
            return;
        posts = posts.filter(p => !this._displayedPosts.postIds.includes(p.id));
        const mp = Env.broadcast.menu.modelProfile;
        const isFirst = this._displayedPosts.total === 0;
        while (this._displayedPosts.total + posts.length > this._displayPostLimit) {
            const postPanel = this._modelPosts.children[this._scrollDirection === "up" ? 0 : (this._modelPosts.children.length - 1)];
            const id = postPanel._tag;
            if (postPanel !== undefined && postPanel.children !== undefined && postPanel.children.length >= 3) {
                const container = postPanel.getChildByName("imageContainer");
                if (container !== undefined && container !== null) {
                    const image = container.getChildByName(`modelPostImage`);
                    if (image !== undefined && image !== null) {
                        this._postImages.splice(this._postImages.indexOf(image), 1);
                    }
                    const lock = container.getChildByName("lockStackPanel");
                    if (lock !== undefined && lock !== null) {
                        this._lockPanels.splice(this._lockPanels.indexOf(lock), 1);
                    }
                }
            }
            this._modelPosts.removeControl(postPanel);
            this._displayedPosts.total = this._modelPosts.children.length;
            this._displayedPosts.postIds.slice(this._displayedPosts.postIds.indexOf(id), 1);
            if (this._scrollDirection === "up")
                this._displayedPosts.last--;
            else
                this._displayedPosts.first++;
        }
        this._postImages.length = 0;
        this._lockPanels.length = 0;
        for (const post of posts) {
            const postPanel = new StackPanel(`modelPostPanel${post.id}`);
            postPanel._tag = post.id;
            const postHeader = new StackPanel(`modelPostPanelHeader${post.id}`);
            postHeader.isVertical = false;
            postHeader.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            postHeader.heightInPixels = this._mesh.m2px(mp.post.header.image.diameter + mp.post.header.bottom);
            postHeader.paddingBottomInPixels = this._mesh.m2px(mp.post.header.bottom);
            const postPfpContainer = new Ellipse(`modelPostPfpContainer${post.id}`);
            postPfpContainer.widthInPixels = this._mesh.m2px(mp.post.header.image.diameter + mp.post.header.left);
            postPfpContainer.heightInPixels = this._mesh.m2px(mp.post.header.image.diameter);
            postPfpContainer.paddingLeftInPixels = this._mesh.m2px(mp.post.header.left);
            postPfpContainer.clipChildren = true;
            postPfpContainer.thickness = 0;
            postHeader.addControl(postPfpContainer);
            const postPfp = new Image3D(`modelPostPfp`);
            postPfp.setSource(this._profilePfpUrl, Env.lobby.gallery.defaultPreview.path);
            //            postPfp.widthInPixels = this._mesh.m2px(mp.post.header.image.diameter) + 1;
            postPfp.heightInPixels = this._mesh.m2px(mp.post.header.image.diameter);
            postPfp.onImageLoadedObservable.addOnce((image) => {
                const aspectRatio = image.domImage.width / image.domImage.height;
                postPfp.widthInPixels = postPfp.heightInPixels * aspectRatio;
            });
            postPfpContainer.addControl(postPfp);
            postHeader.addControl(ObjectBuilder.createTextBlock3D(this._mesh, "modelPostName", this._modelNickname, mp.post.header.text));
            postPanel.addControl(postHeader);
            if (post.image_url !== "") {
                const imageContainer = new Container("imageContainer");
                imageContainer.heightInPixels = 0; //workaround for a babylon warning
                const postImage = new Image3D(`modelPostImage`, post.image_url);
                postImage.heightInPixels = 0; //workaround for a babylon warning
                postImage.autoScale = true;
                if (this._isGuest)
                    postImage.blur = BroadcastMenuModelProfile._blurValue;
                //TODO DZZ DCVR-4474 Remove onerror (?)
                postImage.domImage.onerror = (event) => {
                    console.log("postImage.onError");
                    console.dir(event);
                };
                postImage.onImageLoadedObservable.addOnce(i => {
                    //TODO DZZ DCVR-4474 Remove log
                    console.log(`postImage.onImageLoaded started => ` +
                        `widthInPixels: ${i.widthInPixels}, heightInPixels: ${i.heightInPixels}, ` +
                        `domImage.width: ${i.domImage.width}, domImage.height: ${i.domImage.height}, ` +
                        `domImage.naturalWidth: ${i.domImage.naturalWidth}, domImage.naturalHeight: ${i.domImage.naturalHeight}, ` +
                        `url: ${post.image_url}`);
                    // console.log(`%c postImage.onImageLoaded started => ` +
                    //     `widthInPixels: ${i.widthInPixels}, heightInPixels: ${i.heightInPixels}, ` +
                    //     `domImage.width: ${i.domImage.width}, domImage.height: ${i.domImage.height}, ` +
                    //     `domImage.naturalWidth: ${i.domImage.naturalWidth}, domImage.naturalHeight: ${i.domImage.naturalHeight}, ` +
                    //     `url: ${post.image_url}`, "background: lime");
                    const aspectRatio = i.imageHeight / i.imageWidth;
                    i.widthInPixels = this._mesh.m2px(mp.width);
                    i.heightInPixels = Math.floor(i.widthInPixels * aspectRatio);
                    imageContainer.heightInPixels = i.heightInPixels;
                    //TODO DZZ DCVR-4474 Remove log
                    console.log(`postImage.onImageLoaded recalculated => w: ${Math.floor(i.widthInPixels)}, h: ${imageContainer.heightInPixels}`);
                    // console.log(`%c postImage.onImageLoaded recalculated => w: ${Math.floor(i.widthInPixels)}, h: ${imageContainer.heightInPixels}`, "background: yellow");
                });
                this._postImages.push(postImage);
                imageContainer.addControl(postImage);
                const lockStackPanel = new StackPanel("lockStackPanel");
                this._lockPanels.push(lockStackPanel);
                imageContainer.addControl(lockStackPanel);
                const lk = Env.broadcast.menu.modelProfile.lock;
                const lockImage = new Image3D("lockImage", lk.lockImg);
                lockImage.widthInPixels = this._mesh.m2px(lk.width);
                lockImage.heightInPixels = this._mesh.m2px(lk.height);
                lockStackPanel.addControl(lockImage);
                lockStackPanel.addControl(ObjectBuilder.createTextBlock3D(this._mesh, "lockText", text.broadcast_menu_modelPanel_lockText, Env.broadcast.menu.modelProfile.lockText));
                lockStackPanel.isVisible = this._isGuest;
                postPanel.addControl(imageContainer);
            }
            postPanel.addControl(ObjectBuilder.createTextBlock3D(this._mesh, `modelPostBody${post.id}`, post.body, mp.post.text));
            this._modelPostsHeader.isVisible = true;
            this._modelPosts.addControl(postPanel);
        }
        if (this._scrollDirection === "up")
            this._displayedPosts.first -= posts.length;
        else
            this._displayedPosts.last += posts.length;
        this._displayedPosts.total = this._modelPosts.children.length;
        for (const post of posts)
            this._displayedPosts.postIds.push(post.id);
        if (!isFirst)
            this._modelPostsScrollViewer.verticalValue = (this._modelBio.heightInPixels + (this._scrollDirection === "up" ? 0 : this._modelPosts.heightInPixels)) / this._profileAndPostsPanel.heightInPixels;
        this._modelPostsScrollViewer.valueToScrollTo = 0;
    }
    // private _getModelCategory(sex?: string): string {
    //     const maleTexts = ["male", "man", "men", "boy", "boys"];
    //     const coupleTexts = ["couple", "couples"];
    //     const transTexts = ["trans"];
    //     if (sex === undefined) sex = "female";
    //     sex = sex.toLowerCase();
    //     if (maleTexts.find(x => x === sex))
    //         return "men";
    //     if (coupleTexts.find(x => x === sex))
    //         return "couples";
    //     if (transTexts.find(x => x === sex))
    //         return "trans";
    //     return "girls";
    // }
    _capitalize(str) {
        return str.substring(0, 1).toUpperCase() + str.substring(1);
    }
    outputModelProfile(modelId, profile, filter, hasVideoClips) {
        this._modelBio.clearControls();
        this._modelPosts.clearControls();
        this._modelPostsHeader.isVisible = false;
        this._displayedPosts = { first: 0, last: 0, total: 0, postIds: [] };
        this._modelPfp.setSource(profile.photoUrl, Env.lobby.gallery.defaultPreview.path);
        this._profilePfpUrl = profile.photoUrl;
        this._modelId = modelId;
        this._modelNickname = profile.nickname;
        // this._modelCategory = this._getModelCategory(profile.sex);
        this._modelCategory = profile.tagCategory.code;
        this._tagsFilter = filter.tagsFilter;
        this._streamTypesFilter = filter.streamTypesFilter;
        for (const postPanel of this._modelPosts.children) {
            if (postPanel !== undefined) {
                const image = postPanel.getChildByName(`modelPostPfp`);
                if (image !== undefined && image !== null)
                    image.setSource(profile.photoUrl, Env.lobby.gallery.defaultPreview.path);
                const text = postPanel.getChildByName(`modelPostName`);
                if (text !== undefined && text !== null)
                    text.text = profile.nickname;
            }
        }
        this._modelName.text = profile.nickname;
        this._modelInfo.text = profile.status;
        if (profile.name)
            this.addTag(this._modelBio, { text: profile.name?.toString(), icon: "name" });
        const now = new Date(Date.now());
        const birthDate = profile.birthDate !== undefined ? new Date(profile.birthDate) : undefined;
        if (birthDate !== undefined && birthDate.getMonth() === now.getMonth() && birthDate.getDay() === now.getDay())
            this.addTag(this._modelBio, { text: BroadcastMenuModelProfile._getBirthdayText(profile.age), icon: "birthday", isHighlighted: true });
        else
            this.addTag(this._modelBio, { text: BroadcastMenuModelProfile._getAgeText(profile.age), icon: "birthday" });
        if (profile.sex)
            this.addTag(this._modelBio, { text: this._capitalize(profile.sex?.toString()), icon: "gender" });
        if (profile.interests)
            this.addTag(this._modelBio, { text: profile.interests?.toString(), icon: "interested" });
        for (const language of profile.languages)
            this.addTag(this._modelBio, { text: language, icon: "language" });
        this.createShowMoreButton();
        for (const section of this._modelFullProfilePanelTagSections) {
            section[1].panel.clearControls();
        }
        // this._outputModelProfileTagSection("Appearance", profile.bodyType, profile.ethnicity, profile.eyeColor, profile.hairColor);
        this._outputModelProfileTextSection(text.broadcast_menu_modelPanel_profilePanel_location, profile.country);
        const appearanceGroups = ["m_ethnicity", "m_body_type", "m_hair", "m_eyes"];
        const appearance = profile.tagGroups.filter(g => appearanceGroups.some(c => c === g.code)).flatMap(g => g.tags);
        this._outputModelProfileTagSection(text.broadcast_menu_modelPanel_profilePanel_appearance, ...appearance.map(t => { return { text: t.name, tag: t }; }));
        for (const group of profile.tagGroups.filter(g => appearanceGroups.every(c => c !== g.code)))
            this._outputModelProfileTagSection(group.name, ...group.tags.map(t => { return { text: t.name, tag: t }; }));
        this._outputModelProfileTextSection(text.broadcast_menu_modelPanel_profilePanel_hobbies, profile.hobbies);
        this._outputModelProfileTextSection(text.broadcast_menu_modelPanel_profilePanel_schedule, profile.info);
        this._checkMyVideosPanel.isVisible = hasVideoClips;
    }
    static _getAgeText(age) {
        return StringUtils.stringFormat(text.broadcast_menu_modelPanel_profilePanel_age, age ?? 18);
    }
    static _getBirthdayText(age) {
        return StringUtils.stringFormat(text.broadcast_menu_modelPanel_profilePanel_birthday, age ?? 19);
    }
    async _openLobbyAsync(categoryCodes, tagCodes, streamCodes) {
        await this._callbacks.openLobbyAsync({ categoryCodes, tagCodes, streamCodes });
    }
    removeShowMoreButton() {
        if (this._showMoreButton != undefined) {
            this._modelBio.removeControl(this._showMoreButton);
            this._showMoreButton.dispose();
            this._showMoreButton = undefined;
        }
    }
    createShowMoreButton() {
        this.removeShowMoreButton();
        const vmb = Env.broadcast.menu.modelProfile.viewMoreButton;
        this._showMoreButton = ButtonUtils.CreateImgButtonWithText("modelProfileShowMoreButton", {
            imageLayout: vmb.colorLayout,
            textLayout: vmb.textLayout,
            text: text.broadcast_menu_modelPanel_profilePanel_viewMore
        }, this._mesh);
        this._showMoreButton.onPointerClickObservable.add(() => {
            this.removeShowMoreButton();
            this._modelFullProfilePanel.isVisible = true;
            this._backButton.isVisible = true;
            this._modelInfoContainer.isVisible = false;
            this._headerText.text = text.broadcast_menu_modelPanel_profilePanel_info;
        });
        this._showMoreButton.isVisible = !this._modelFullProfilePanel.isVisible;
        this._modelBio.addControl(this._showMoreButton);
    }
    stateChanged(state, isGuest, _reason) {
        this._isGuest = isGuest;
        this._updateState(state);
        for (const image of this._postImages)
            image.blur = isGuest ? BroadcastMenuModelProfile._blurValue : 0;
        for (const lock of this._lockPanels)
            lock.isVisible = isGuest;
    }
    addTag(panel, params) {
        if (params.text === undefined)
            return;
        const t = Env.broadcast.menu.modelProfile.tag;
        const tagContainer = ButtonUtils.CreateImgToggleButton("tagButton", t, this._mesh);
        const tagPanel = new StackPanel();
        tagPanel.isVertical = false;
        let image = null;
        if (params.icon !== undefined) {
            image = new Image3D("", t.icon.image(params.icon));
            image.widthInPixels = this._mesh.m2px(t.icon.width + t.icon.padding.right + t.icon.padding.left);
            image.heightInPixels = this._mesh.m2px(t.icon.height + t.icon.padding.top + t.icon.padding.bottom);
            image.paddingTopInPixels = this._mesh.m2px(t.icon.padding.top);
            image.paddingRightInPixels = this._mesh.m2px(t.icon.padding.right);
            image.paddingBottomInPixels = this._mesh.m2px(t.icon.padding.bottom);
            image.paddingLeftInPixels = this._mesh.m2px(t.icon.padding.left);
            tagPanel.addControl(image);
        }
        const tag = ObjectBuilder.createTextBlock3D(this._mesh, "", "", t.text);
        tag.text = params.text;
        tag.widthInPixels = ContextUtils.MeasureText(tag, this._mesh.texture.getContext());
        tagPanel.addControl(tag);
        tagContainer.widthInPixels = tag.widthInPixels + this._mesh.m2px(t.tagPadding.left + t.tagPadding.right);
        if (image !== null)
            tagContainer.widthInPixels += image.widthInPixels;
        tagContainer.heightInPixels = this._mesh.m2px(t.tagHeight + t.tagPadding.top + t.tagPadding.bottom);
        tagContainer.addControl(tagPanel);
        if (params.tag !== undefined) {
            const isToggled = this._tagsFilter !== undefined && new RegExp(`(?<=(?:^|,))${params.tag.code}(?=(?:$|,))`).test(this._tagsFilter);
            tagContainer.isToggled = isToggled;
            tagContainer.onPointerClickObservable.add(v => this._onTagClickHandler(new Vector2WithInfo(new Vector2(tag.centerX, tag.centerY), v.buttonIndex), params.tag, isToggled));
        }
        else
            tagContainer.isEnabled = false;
        if (params.isHighlighted)
            tagContainer.stateController.changeState("highlighted");
        panel.addControl(tagContainer);
    }
    _updateState(state) {
        if (state === this._currentState) {
            return;
        }
        this._currentState = state;
        const statusData = this.statusMap[state];
        if (!statusData) {
            return;
        }
        this._statusContainer.background = statusData.color;
        this._outlineContainer.color = statusData.color;
        this._statusText.text = statusData.text;
    }
}
BroadcastMenuModelProfile._blurValue = 24;
